/* eslint-disable no-unused-vars */
import { Button, Group, Image, Loader, Stack, Text, TextInput, Textarea } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import React, { useState } from 'react';
import { HiOutlinePlus } from 'react-icons/hi';
import { IconUpload, IconX, IconPhoto, IconLoader } from '@tabler/icons-react';
import { IMaterialFormData } from 'types/lcms/courses';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import fileToBase64 from 'utils/fileToBase64';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sharepointService, UploadSharepointForm } from 'services/sharepoint/sharepoint.service';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';
import { courseService } from 'services/lcms/course.service';

const materialItemSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  file: z.instanceof(File, { message: 'File is required' }),
  description: z.string().optional()
});

const materialFormSchema = z.object({
  materials: z.array(materialItemSchema).min(1, 'At least one material is required')
});

type MaterialFormData = z.infer<typeof materialFormSchema>;

interface MaterialInputDataFormProps {
  hashKey?: string;
  SMEId?: string;
  courseId?: string;
  role?: 'admin' | 'sme';
  onSubmit?: (_data: {
    hash?: string;
    courseId?: string;
    materials: {
      title: string;
      url: string;
      type: string;
      description?: string;
    }[];
  }) => void;
  onCancel: () => void;
}

const MaterialInputDataForm: React.FC<MaterialInputDataFormProps> = ({
  hashKey,
  SMEId,
  onSubmit,
  onCancel,
  courseId,
  role = 'sme'
}) => {
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [hasFilesToUpload, setHasFilesToUpload] = useState(false);
  const [previewUrls, setPreviewUrls] = useState<(string | null)[]>([null]);

  const { data: sharepointToken, isFetching: isPendingSharepointToken } = useQuery({
    queryKey: ['getTokenSharepoint'],
    queryFn: () => sharepointService.getTokenSharepoint(),
    enabled: hasFilesToUpload
  });

  const uploadFiles = useMutation({
    mutationFn: (data: UploadSharepointForm) => sharepointService.uploadFile(data),
    onSuccess: () => {
      showSuccessNotification({
        title: 'Success upload!',
        message: 'Your file has been uploaded successfully'
      });
    },
    onError: () => {
      showErrorNotification({
        title: 'Failed when upload file',
        message: 'Failed when upload your file, please try again later.'
      });
    }
  });

  const submitMaterialData = useMutation({
    mutationFn: (data: { hash: string; materials: IMaterialFormData[] }) =>
      courseService.smeSubmitMaterial(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getSMESubmitDetail', hashKey] });
      queryClient.invalidateQueries({ queryKey: ['courseDetail', courseId] });
      queryClient.invalidateQueries({
        queryKey: ['getPICDetail', hashKey]
      });
      showSuccessNotification({
        title: 'Success submit material!',
        message: 'Yeay! your material submitted successfully'
      });
    },
    onError: () => {
      showErrorNotification({
        title: 'Failed when submit material',
        message: 'Failed when submit your material, please try again later.'
      });
    }
  });

  const adminSubmitMaterialData = useMutation({
    mutationFn: (data: { courseId: string; materials: IMaterialFormData[] }) =>
      courseService.adminSubmitMaterial(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getAdminSubmitDetail', courseId] });
      showSuccessNotification({
        title: 'Success submit material!',
        message: 'Yeay! your material submitted successfully'
      });
    },
    onError: () => {
      showErrorNotification({
        title: 'Failed when submit material',
        message: 'Failed when submit your material, please try again later.'
      });
    }
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<MaterialFormData>({
    resolver: zodResolver(materialFormSchema),
    defaultValues: {
      materials: [{ title: '', file: undefined, description: '' }]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'materials'
  });

  const acceptedTypes = {
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'video/mp4': ['.mp4'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/pdf': ['.pdf'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
    'application/vnd.ms-powerpoint': ['.ppt'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/msword': ['.doc'],
    'application/zip': ['.zip'],
    'application/x-zip-compressed': ['.zip']
  };

  const getFileType = (file: File): string => {
    const mimeType = file.type;
    if (mimeType.startsWith('image/')) return 'Image';
    if (mimeType.startsWith('video/')) return 'Video';
    if (mimeType.includes('spreadsheetml')) return 'Excel';
    if (mimeType === 'application/pdf') return 'PDF';
    if (mimeType.includes('presentationml') || mimeType === 'application/vnd.ms-powerpoint')
      return 'PowerPoint';
    if (mimeType.includes('wordprocessingml') || mimeType === 'application/msword') return 'Word';
    if (mimeType === 'application/zip' || mimeType === 'application/x-zip-compressed') return 'ZIP';
    return 'Unknown';
  };

  const createPreviewUrl = (file: File, index: number) => {
    if (file && file.type.startsWith('image/')) {
      const newPreviewUrls = [...previewUrls];
      const url = URL.createObjectURL(file);
      newPreviewUrls[index] = url;
      setPreviewUrls(newPreviewUrls);
    } else {
      const newPreviewUrls = [...previewUrls];
      newPreviewUrls[index] = null;
      setPreviewUrls(newPreviewUrls);
    }
  };

  const handleAddMaterial = () => {
    append({ title: '', file: new File([], ''), description: '' });
    setPreviewUrls([...previewUrls, null]);
  };

  const handleRemoveMaterial = (index: number) => {
    remove(index);
    const newPreviewUrls = [...previewUrls];
    newPreviewUrls.splice(index, 1);
    setPreviewUrls(newPreviewUrls);
  };

  const onFormSubmit = async (data: MaterialFormData) => {
    setIsLoading(true);
    try {
      if (!sharepointToken?.data?.token) {
        showErrorNotification({
          title: 'Token Error',
          message: 'SharePoint token not available. Please try again.'
        });
        return;
      }

      const uploadedMaterials = await Promise.all(
        data.materials.map(async (material) => {
          try {
            const base64Data = await fileToBase64(material.file);

            const uploadData: UploadSharepointForm = {
              sharepointToken: sharepointToken?.data?.token,
              folderName: 'materials',
              fileName: material.file.name,
              fileBase64: base64Data
            };

            const uploadResult = await uploadFiles.mutateAsync(uploadData);

            return {
              title: material.title,
              url: uploadResult?.data?.fileUrl || '',
              type: getFileType(material.file),
              description: material.description || ''
            };
          } catch (uploadError) {
            throw new Error(`Failed to upload ${material.file.name}`);
          }
        })
      );

      if (role === 'sme' && hashKey) {
        const materialSubmissionData = {
          hash: hashKey,
          materials: uploadedMaterials
        };

        await submitMaterialData.mutateAsync(materialSubmissionData);

        if (onSubmit) {
          onSubmit({
            hash: hashKey,
            materials: uploadedMaterials
          });
        }
      } else if (role === 'admin' && courseId) {
        const materialSubmissionData = {
          SMEId,
          courseId,
          materials: uploadedMaterials
        };

        await adminSubmitMaterialData.mutateAsync(materialSubmissionData);

        if (onSubmit) {
          onSubmit({
            courseId,
            materials: uploadedMaterials
          });
        }
      }

      reset({
        materials: [{ title: '', file: undefined, description: '' }]
      });
      setHasFilesToUpload(false);
      setPreviewUrls([null]);
      onCancel();
    } catch (error) {
      showErrorNotification({
        title: 'Submission Error',
        message: 'Failed to submit materials. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const bytesToMB = (bytes: number): string => {
    return (bytes / (1024 * 1024)).toFixed(2);
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)}>
      <Stack gap={16}>
        {fields.map((field, index) => (
          <div
            key={field.id}
            style={{ padding: '16px', border: '1px solid #e0e0e0', borderRadius: '8px' }}
          >
            <Group justify='space-between' mb={12}>
              <Text fw={600}>Material {index + 1}</Text>
              {fields.length > 1 && (
                <Button
                  variant='subtle'
                  color='destructive'
                  size='xs'
                  onClick={() => handleRemoveMaterial(index)}
                >
                  Remove
                </Button>
              )}
            </Group>

            <Stack gap={12}>
              <Controller
                name={`materials.${index}.title`}
                control={control}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label='Title'
                    placeholder='Enter material title'
                    required
                    error={errors.materials?.[index]?.title?.message}
                  />
                )}
              />

              <Controller
                name={`materials.${index}.file`}
                control={control}
                render={({ field: { onChange, value, ...field } }) => (
                  <div>
                    <Text size='sm' fw={500} mb={8}>
                      File <span style={{ color: 'red' }}>*</span>
                    </Text>
                    <Dropzone
                      onDrop={(files) => {
                        if (files.length > 0) {
                          onChange(files[0]);
                          setHasFilesToUpload(true);
                          createPreviewUrl(files[0], index);
                        }
                      }}
                      onReject={(files) => {
                        showErrorNotification({
                          title: 'Invalid File Type',
                          message:
                            'Only PNG, JPG, MP4, XLSX, PDF, PPT, Word, and ZIP files are allowed.'
                        });
                      }}
                      maxSize={20 * 1024 ** 2} // 20MB
                      accept={acceptedTypes}
                      multiple={false}
                    >
                      <Group justify='center' gap='xl' mih={120} style={{ pointerEvents: 'none' }}>
                        <Dropzone.Accept>
                          <IconUpload size={32} color='var(--mantine-color-blue-6)' stroke={1.5} />
                        </Dropzone.Accept>
                        <Dropzone.Reject>
                          <IconX size={32} color='var(--mantine-color-red-6)' stroke={1.5} />
                        </Dropzone.Reject>
                        <Dropzone.Idle>
                          {previewUrls[index] ? (
                            <div style={{ marginTop: 12 }}>
                              <Text size='sm' fw={500} mb={8}>
                                Preview
                              </Text>
                              <Image
                                src={previewUrls[index]}
                                alt='Preview'
                                height={150}
                                fit='contain'
                                radius='md'
                              />
                            </div>
                          ) : isPendingSharepointToken ? (
                            <Loader size={'sm'} />
                          ) : (
                            <IconPhoto size={32} stroke={1.5} />
                          )}
                        </Dropzone.Idle>

                        <div>
                          <Text size='md' inline>
                            {value
                              ? value.name || 'Drag files here or click to select'
                              : 'Drag files here or click to select'}
                          </Text>
                          {!value || !value.name ? (
                            <Text size='xs' c='dimmed' inline mt={4}>
                              PNG, JPG, MP4, XLSX, PDF, PPT, DOC, ZIP (max 20MB)
                            </Text>
                          ) : (
                            <Text size='xs' c='dimmed' inline mt={4}>
                              Size: {bytesToMB(value.size)} MB
                            </Text>
                          )}
                        </div>
                      </Group>
                    </Dropzone>
                    {errors.materials?.[index]?.file && (
                      <Text size='sm' c='red' mt={4}>
                        {errors.materials[index]?.file?.message}
                      </Text>
                    )}
                  </div>
                )}
              />

              <Controller
                name={`materials.${index}.description`}
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    label='Description'
                    placeholder='Enter material description (optional)'
                    minRows={3}
                    maxRows={6}
                  />
                )}
              />
            </Stack>
          </div>
        ))}

        <Button variant='outline' leftSection={<HiOutlinePlus />} onClick={handleAddMaterial}>
          Add Another Material
        </Button>

        <Group justify='flex-end'>
          <Button variant='outline' onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type='submit'
            loading={isLoading || uploadFiles.isPending || submitMaterialData.isPending}
            disabled={!hasFilesToUpload || isPendingSharepointToken}
          >
            {uploadFiles.isPending
              ? 'Uploading Files...'
              : submitMaterialData.isPending
                ? 'Submitting Materials...'
                : 'Submit All Materials'}
          </Button>
        </Group>
      </Stack>
    </form>
  );
};

export default MaterialInputDataForm;
